import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Loader2, Shield, Zap, Eye, BarChart3, AlertCircle, Swords, ChevronDown, Phone, Star } from "lucide-react";

const GEOFF_PHOTO = "https://customer-assets.emergentagent.com/job_traffic-magnet-8/artifacts/vubacfhk_profile-pic.png";

const stats = [
  { number: "28+", label: "Years Helping Businesses", icon: Shield },
  { number: "300+", label: "Clients Served", icon: BarChart3 },
  { number: "100M+", label: "Weekly AI Searchers", icon: Eye },
  { number: "95%", label: "Client Retention Rate", icon: Star },
];

const scanStages = [
  "Connecting to website...",
  "Analyzing SEO structure...",
  "Checking mobile responsiveness...",
  "Scanning AI search visibility...",
  "Querying ChatGPT database...",
  "Checking Perplexity coverage...",
  "Analyzing Gemini visibility...",
  "Generating your report...",
];

const scanStagesCompare = [
  "Connecting to your website...",
  "Connecting to competitor...",
  "Analyzing SEO structures...",
  "Checking mobile responsiveness...",
  "Scanning AI search visibility...",
  "Querying ChatGPT for both sites...",
  "Running Perplexity comparison...",
  "Analyzing Gemini coverage...",
  "Building head-to-head report...",
];

export default function LandingPage({ onSubmit, loading, error }) {
  const [form, setForm] = useState({ url: "", name: "", email: "", competitor_url: "" });
  const [errors, setErrors] = useState({});
  const [scanStage, setScanStage] = useState(0);
  const [showCompetitor, setShowCompetitor] = useState(false);

  const stages = showCompetitor && form.competitor_url ? scanStagesCompare : scanStages;

  useEffect(() => {
    if (!loading) { setScanStage(0); return; }
    const interval = setInterval(() => {
      setScanStage(prev => (prev < stages.length - 1 ? prev + 1 : prev));
    }, 2500);
    return () => clearInterval(interval);
  }, [loading, stages.length]);

  const validate = () => {
    const errs = {};
    if (!form.url.trim()) errs.url = "Website URL is required";
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const payload = { ...form };
      if (!payload.competitor_url?.trim()) delete payload.competitor_url;
      onSubmit(payload);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none z-50"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />
      <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-orange-500/5 blur-[150px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <a href="https://geokath.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center font-black text-white text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>G</div>
          <div>
            <span className="text-white font-bold text-lg tracking-tight block leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Geokath Marketing</span>
            <span className="text-[10px] text-zinc-500 tracking-wider">Sacramento's SEO Experts Since 1996</span>
          </div>
        </a>
        <div className="hidden sm:flex items-center gap-4">
          <a href="/leaderboard" className="text-sm text-zinc-400 hover:text-orange-500 transition-colors" data-testid="nav-leaderboard">Leaderboard</a>
          <a href="https://geokath.com/services" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-orange-500 transition-colors">Services</a>
          <a href="tel:909-800-5616" className="text-sm text-orange-500 font-semibold flex items-center gap-1 hover:text-orange-400 transition-colors" data-testid="header-phone">
            <Phone className="w-3.5 h-3.5" /> 909-800-5616
          </a>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">

          {/* Left */}
          <div>
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-6 block" data-testid="hero-overline">
              Free AI Visibility Audit
            </motion.span>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl md:text-6xl xl:text-7xl font-black tracking-tighter leading-[0.9] mb-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="hero-heading">
              Is Your Brand{" "}
              <span className="text-orange-500">Visible</span>{" "}
              to AI?
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }}
              className="text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg" data-testid="hero-subtitle">
              ChatGPT, Perplexity, and Gemini are changing how customers find businesses.
              Get an instant AEO, GEO & SEO audit — and compare against your competitor.
            </motion.p>

            {/* Geoff trust block */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.35 }}
              className="flex items-center gap-4 mb-10 border border-zinc-800 rounded-xl p-4 bg-[#1a1a1c]/40" data-testid="geoff-trust-block">
              <img src={GEOFF_PHOTO} alt="Geoff Clark" className="w-14 h-14 rounded-full object-cover border-2 border-orange-500/30" />
              <div>
                <p className="text-sm text-zinc-300 font-semibold">Built by Geoff Clark, MBA</p>
                <p className="text-xs text-zinc-500">Founder of Geokath Marketing. Helping Sacramento businesses dominate online since 1996.</p>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}
              className="grid grid-cols-2 gap-4" data-testid="stats-section">
              {stats.map((s, i) => (
                <div key={i} className="border border-zinc-800 rounded-lg p-4 bg-[#1a1a1c]/40">
                  <s.icon className="w-4 h-4 text-orange-500/70 mb-2" />
                  <div className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.number}</div>
                  <div className="text-xs text-zinc-500">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <div className="border border-zinc-800 rounded-2xl p-8 md:p-10 backdrop-blur-xl bg-[#1a1a1c]/70 shadow-2xl shadow-black/50" data-testid="audit-form-card">
              <h2 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Start Your Free Audit
              </h2>
              <p className="text-zinc-500 text-sm mb-8">Results in 30 seconds. No credit card required.</p>

              {loading ? (
                <div className="py-6" data-testid="scan-loading">
                  <div className="flex items-center justify-center mb-8">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full border-4 border-zinc-800 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                      </div>
                      <div className="absolute -inset-2 rounded-full border border-orange-500/20 animate-pulse" />
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {stages.map((stage, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: i <= scanStage ? 1 : 0.2, x: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.08 }}
                        className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${i < scanStage ? "bg-green-500" : i === scanStage ? "bg-orange-500 animate-pulse" : "bg-zinc-700"}`} />
                        <span className={`text-sm ${i <= scanStage ? "text-zinc-300" : "text-zinc-700"}`}>{stage}</span>
                        {i < scanStage && <span className="text-green-500 text-xs ml-auto">Done</span>}
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-5 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-orange-500 rounded-full"
                      initial={{ width: "0%" }} animate={{ width: `${((scanStage + 1) / stages.length) * 100}%` }}
                      transition={{ duration: 0.5 }} style={{ boxShadow: "0 0 10px rgba(249,115,22,0.5)" }} />
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">Your Website URL</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                      <input type="text" placeholder="yourwebsite.com"
                        value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                        className={`w-full bg-[#0f0f10] border ${errors.url ? "border-red-500" : "border-zinc-700 focus:border-orange-500"} rounded-lg pl-11 pr-4 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors`}
                        data-testid="audit-url-input" />
                    </div>
                    {errors.url && <p className="text-red-500 text-xs mt-1">{errors.url}</p>}
                  </div>

                  <AnimatePresence>
                    {!showCompetitor ? (
                      <motion.button type="button" onClick={() => setShowCompetitor(true)}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex items-center gap-2 text-sm text-orange-500/80 hover:text-orange-500 transition-colors group"
                        data-testid="add-competitor-button">
                        <Swords className="w-4 h-4" />
                        <span>Compare with a competitor</span>
                        <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
                      </motion.button>
                    ) : (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                        <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-2">
                          <Swords className="w-3 h-3 text-orange-500" /> Competitor URL <span className="text-zinc-700 normal-case tracking-normal font-normal">(optional)</span>
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                          <input type="text" placeholder="competitor.com"
                            value={form.competitor_url} onChange={e => setForm(p => ({ ...p, competitor_url: e.target.value }))}
                            className="w-full bg-[#0f0f10] border border-zinc-700 focus:border-orange-500 rounded-lg pl-11 pr-4 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors"
                            data-testid="competitor-url-input" />
                        </div>
                        <p className="text-zinc-600 text-xs mt-1">See how you stack up head-to-head</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">First Name</label>
                    <input type="text" placeholder="Your name"
                      value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      className={`w-full bg-[#0f0f10] border ${errors.name ? "border-red-500" : "border-zinc-700 focus:border-orange-500"} rounded-lg px-4 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors`}
                      data-testid="audit-name-input" />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">Email Address</label>
                    <input type="email" placeholder="you@company.com"
                      value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className={`w-full bg-[#0f0f10] border ${errors.email ? "border-red-500" : "border-zinc-700 focus:border-orange-500"} rounded-lg px-4 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors`}
                      data-testid="audit-email-input" />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3" data-testid="audit-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                    </div>
                  )}

                  <button type="submit"
                    className="w-full bg-orange-500 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 hover:bg-orange-600 shadow-[0_0_30px_rgba(249,115,22,0.35)] hover:shadow-[0_0_50px_rgba(249,115,22,0.5)] flex items-center justify-center gap-3"
                    data-testid="audit-submit-button">
                    {showCompetitor && form.competitor_url ? "Run Comparison Audit" : "Run Free Audit"} <ArrowRight className="w-5 h-5" />
                  </button>

                  <p className="text-zinc-600 text-xs text-center">No credit card required. Results in 30 seconds.</p>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        {/* Why section */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-24 md:mt-32">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Why AI Search Visibility Matters in 2026
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">AI-driven search engine marketing has changed the game. Geokath Marketing is a pioneering generative engine optimization agency — and now we're giving you free tools to measure your AI search engine visibility metrics.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { tag: "AEO", title: "Answer Engine Optimization", desc: "AEO services for small business ensure ChatGPT, Perplexity, and Google AI Overviews can find, extract, and cite your brand. This is how you rank in AI search results." },
              { tag: "GEO", title: "Generative Engine Optimization", desc: "GEO marketing strategies control how AI generates content about your brand. Build your generative search influence score and get cited by AI search engines." },
              { tag: "SEO", title: "Traditional SEO + AI Crawlers", desc: "Optimize website for AI crawlers while maintaining traditional search rankings. Semantic SEO for AI models plus structured data for generative engines." },
            ].map((card, i) => (
              <div key={i} className="border border-zinc-800 rounded-xl p-8 bg-[#1a1a1c]/40 hover:-translate-y-1 transition-transform duration-300">
                <span className="text-orange-500 font-black text-sm tracking-wider">{card.tag}</span>
                <h3 className="text-lg font-bold text-white mt-2 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{card.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Geokath services teaser */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-20 border border-zinc-800 rounded-2xl p-8 md:p-12 bg-[#1a1a1c]/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-3 block">Website + AEO</span>
              <h3 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>$1,500</h3>
              <p className="text-sm text-zinc-500">Professional website + hosting + AI search optimization. One of America's only all-in-one solutions.</p>
              <a href="https://geokath.com/offers/website-package" target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 hover:text-orange-400 mt-3 inline-flex items-center gap-1">
                Learn more <ArrowRight className="w-3 h-3" />
              </a>
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-3 block">SEO Services</span>
              <h3 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>From $350/mo</h3>
              <p className="text-sm text-zinc-500">Monthly SEO retainers that deliver results. Local keyword optimization, content, citation building.</p>
              <a href="https://geokath.com/services" target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 hover:text-orange-400 mt-3 inline-flex items-center gap-1">
                View services <ArrowRight className="w-3 h-3" />
              </a>
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-3 block">Free Consultation</span>
              <h3 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>30 Minutes</h3>
              <p className="text-sm text-zinc-500">Talk directly with Geoff Clark. No junior managers, no pressure. Just honest advice from 28+ years of experience.</p>
              <a href="https://geokath.com/contact" target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 hover:text-orange-400 mt-3 inline-flex items-center gap-1">
                Book now <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* SEO/AEO Rich Content Section */}
        <motion.section initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1 }}
          className="mt-24 md:mt-32" data-testid="seo-content-section">

          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Free AI Search Visibility Audit & Ranking Report
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8 max-w-3xl">
            Our <strong className="text-zinc-300">AI search visibility audit</strong> generates a complete{" "}
            <strong className="text-zinc-300">AI search ranking report</strong> for any website. We analyze your brand visibility in AI overviews,
            check if AI search engines can find your business, and measure your{" "}
            <strong className="text-zinc-300">generative search influence score</strong> across ChatGPT, Perplexity, and Google AI Overviews.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <article className="border border-zinc-800 rounded-xl p-6 bg-[#1a1a1c]/30">
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What Our AI Search Citation Audit Checks
              </h3>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">Structured data for generative engines</strong> — schema markup that AI models need</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">Entity-based search optimization</strong> — how AI recognizes your brand</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">AI search algorithm ranking factors</strong> — what determines your AI visibility</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">Semantic SEO for AI models</strong> — content structure AI can extract</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">Website audit for Perplexity AI</strong> — specific visibility check</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">AI search engine visibility metrics</strong> — scored across all platforms</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> <strong className="text-zinc-300">Brand sentiment in AI responses</strong> — how AI describes you</li>
              </ul>
            </article>

            <article className="border border-zinc-800 rounded-xl p-6 bg-[#1a1a1c]/30">
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                AI Search Visibility Statistics 2026
              </h3>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> 40% of all searches now involve AI-powered tools</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> 73% of users trust AI-generated answers over traditional search results</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> ChatGPT has 100M+ weekly users asking for business recommendations</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> Businesses with strong AI visibility see 2.5x higher engagement</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> Google AI Overviews now appear in 30%+ of search results</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> Voice search (powered by AI) accounts for 50%+ of mobile queries</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 mt-1">+</span> 62% of Gen Z prefers AI search over traditional Google</li>
              </ul>
            </article>
          </div>

          <article className="mb-12">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              AEO Services for Small Business: How Answer Engine Optimization Works
            </h3>
            <div className="text-sm text-zinc-400 leading-relaxed space-y-4 max-w-3xl">
              <p>
                <strong className="text-zinc-300">Answer Engine Optimization (AEO)</strong> is the most important new marketing discipline
                for businesses in 2026. Unlike traditional SEO which gets you ranked as a link, AEO ensures your brand becomes the{" "}
                <strong className="text-zinc-300">authoritative source for AI search data</strong> that ChatGPT, Perplexity, and Google AI
                Overviews pull from when answering user questions.
              </p>
              <p>
                A strong <strong className="text-zinc-300">ChatGPT search optimization strategy</strong> includes: implementing comprehensive
                schema markup, building topical authority through expert content, optimizing for{" "}
                <strong className="text-zinc-300">AI search algorithm ranking factors</strong> like E-E-A-T signals, and ensuring your
                robots.txt allows AI crawlers (GPTBot, PerplexityBot, Google-Extended) to access your content.
              </p>
              <p>
                <strong className="text-zinc-300">Generative Engine Optimization (GEO)</strong> takes AEO further by actively shaping how AI
                describes your brand. When someone asks "What's the best [your service] near me?" or "recommend a [your industry] company,"
                GEO optimization determines whether you appear — and how favorably you're described. This is{" "}
                <strong className="text-zinc-300">LLM optimization for brand growth</strong> at its core.
              </p>
              <p>
                The difference between <strong className="text-zinc-300">GEO vs SEO marketing services</strong>: SEO gets you links in search
                results; GEO gets you <em>cited</em> as the answer. Both work together — and our audit measures both. Understanding how{" "}
                <strong className="text-zinc-300">AI search impacts website traffic</strong> is critical: brands optimized for AI see increased
                direct searches, higher trust signals, and new customer acquisition channels.
              </p>
            </div>
          </article>

          <article className="mb-12">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              How to Get Cited by AI Search Engines
            </h3>
            <div className="text-sm text-zinc-400 leading-relaxed space-y-4 max-w-3xl">
              <p>
                Getting cited by AI search engines requires building what experts call a{" "}
                <strong className="text-zinc-300">generative search influence score</strong>. This is determined by multiple{" "}
                <strong className="text-zinc-300">AI search algorithm ranking factors</strong>: your domain authority, content comprehensiveness,
                structured data implementation, entity recognition, and cross-platform brand consistency.
              </p>
              <p>
                An effective strategy to <strong className="text-zinc-300">optimize website for AI crawlers</strong> includes: allowing GPTBot and
                PerplexityBot in your robots.txt, implementing FAQ schema and HowTo markup, creating comprehensive long-form content that
                answers questions completely, and building your <strong className="text-zinc-300">entity-based search optimization</strong> by
                maintaining consistent NAP data and expert credentials across the web.
              </p>
              <p>
                Our free <strong className="text-zinc-300">AI search citation audit</strong> analyzes all these factors and delivers a complete{" "}
                <strong className="text-zinc-300">AI search ranking report</strong> — showing exactly where your website stands and what to fix
                to become an <strong className="text-zinc-300">authoritative source for AI search data</strong>.
              </p>
            </div>
          </article>

          <article className="mb-8">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Who Needs an AI Search Visibility Audit?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "Small business owners", "Marketing agencies", "E-commerce stores",
                "Local service businesses", "Healthcare practices", "Real estate agents",
                "Restaurants & hospitality", "Law firms & attorneys", "SaaS companies",
                "Freelancers & consultants", "Financial advisors", "Home service companies",
                "Dental practices", "Automotive businesses", "B2B companies",
                "Personal brands", "Coaches & trainers", "Insurance agencies"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-zinc-400 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </article>
        </motion.section>

        {/* Footer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1 }}
          className="mt-16 pt-8 border-t border-zinc-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center font-black text-white text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>G</div>
              <div>
                <a href="https://geokath.com" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-orange-500 transition-colors font-semibold">
                  Geokath Marketing
                </a>
                <p className="text-xs text-zinc-600">Sacramento, CA | Since 1996</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-xs text-zinc-600">
              <a href="https://geokath.com/services" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Services</a>
              <a href="https://geokath.com/blog" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Blog</a>
              <a href="https://geokath.com/faq" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">FAQ</a>
              <a href="https://geokath.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Contact</a>
              <span>909-800-5616</span>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
